import React, { useState } from 'react';
import { usePaymentMethods, useBranches } from '../../db/hooks/useLookupData';
import { useDatabase } from '../../db/DatabaseProvider';
import { generateId, now } from '../../lib/uuid';
import { useHasPermission } from '../auth/RequirePermission';
import { useToast } from '../common/ToastProvider';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { CustomSelect } from '../common/CustomSelect';
import { Button } from '../common/Button';

export function PaymentMethodManager() {
  const { paymentMethods, isLoading } = usePaymentMethods();
  const { branches } = useBranches();
  const db = useDatabase();
  const hasPermission = useHasPermission();
  const canManageConfig = hasPermission('settings:manage');
  const toast = useToast();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [isActive, setIsActive] = useState(true);
  const [branchId, setBranchId] = useState('');

  const openNew = () => {
    setEditingId(null);
    setName('');
    setCurrency('USD');
    setIsActive(true);
    setBranchId('');
    setIsFormOpen(true);
  };

  const openEdit = (paymentMethod: any) => {
    setEditingId(paymentMethod.id);
    setName(paymentMethod.name);
    setCurrency(paymentMethod.currency || 'USD');
    setIsActive(paymentMethod.is_active !== false);
    setBranchId(paymentMethod.branch_id || '');
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!db || !canManageConfig) return;

    // Anti-orphan validations using $elemMatch
    const transactionCount = await db.transactions.count({
      selector: {
        payments: { $elemMatch: { payment_method_id: id } },
        is_deleted: { $ne: true }
      }
    }).exec();

    if (transactionCount > 0) {
      toast.warning(`Este método de pago está registrado en ${transactionCount} transactions y no puede ser eliminado. Por favor, desactívela en su lugar.`, 'Asociación Activa');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Método de Pago',
      message: '¿Estás seguro de que deseas eliminar este método de pago?',
      onConfirm: async () => {
        try {
          const doc = await db.payment_methods.findOne(id).exec();
          if (doc) {
            await doc.patch({
              is_deleted: true,
              updated_at: now(),
              
            });
            toast.success('Método de pago eliminado correctamente');
          }
        } catch (err: any) {
          toast.error('Error al eliminar método de pago: ' + err.message);
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const type = currency === 'VES' ? 'local' : 'foreign';

    if (editingId) {
      const doc = await db.payment_methods.findOne(editingId).exec();
      if (doc) {
        await doc.patch({
          name, type, currency,
          is_active: isActive,
          branch_id: branchId || null,
          updated_at: now(),
        });
      }
    } else {
      await db.payment_methods.insert({
        id: generateId(),
        name, type, currency,
        is_active: isActive,
        branch_id: branchId || null,
        updated_at: now(),
        is_deleted: false,
        last_modified_by: 'system',
      });
    }
    setIsFormOpen(false);
  };

  if (isLoading) return <div className="p-6 text-center text-xs text-text-tertiary">Cargando métodos de pago...</div>;

  return (
    <div className="border border-border bg-surface rounded-2xl shadow-xs overflow-hidden">
      {/* Harmonized Header Bar */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-border bg-surface-secondary/40">
        <div>
          <h3 className="text-sm font-bold text-text-primary">Métodos de Pago</h3>
          <p className="text-xs text-text-secondary mt-0.5">Opciones de cobro y pago para facturación</p>
        </div>
        {canManageConfig && (
          <Button
            variant="primary"
            size="sm"
            icon={Plus}
            onClick={() => {
              if (isFormOpen) {
                setIsFormOpen(false);
              } else {
                openNew();
              }
            }}
          >
            {isFormOpen ? 'Cerrar Formulario' : 'Nuevo Método'}
          </Button>
        )}
      </div>

      {/* Expandable Creation Form */}
      {isFormOpen && (
        <form onSubmit={handleSubmit} className="p-5 border-b border-border bg-surface-secondary/50 space-y-4 animate-fade-in">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre del Método *</label>
              <input
                required
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full h-10 rounded-xl border border-border bg-surface px-3.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
                placeholder="Ej. Zelle, Efectivo, Punto de Venta"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-text-secondary mb-1">Moneda Operativa *</label>
              <CustomSelect
                value={currency}
                onChange={(val) => setCurrency(val)}
                options={[
                  { value: 'USD', label: 'USD (Dólares)' },
                  { value: 'VES', label: 'VES (Bolívares)' },
                  { value: 'EUR', label: 'EUR (Euros)' },
                ]}
                buttonClassName="!py-2"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-text-secondary mb-1">Sede de Visibilidad</label>
              <CustomSelect
                value={branchId}
                onChange={(val) => setBranchId(val)}
                options={[
                  { value: '', label: 'Todas las sedes' },
                  ...branches.map((s) => ({ value: s.id, label: s.name })),
                ]}
                buttonClassName="!py-2"
              />
            </div>
            <div className="sm:col-span-2 flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="mp_activo"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-border text-brand-orange focus:ring-brand-orange bg-surface cursor-pointer"
              />
              <label htmlFor="mp_activo" className="text-xs font-bold text-text-primary cursor-pointer select-none">
                Método de Pago Habilitado para Cobros
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
            <Button variant="ghost" size="sm" type="button" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit">
              {editingId ? 'Actualizar Método' : 'Guardar Método'}
            </Button>
          </div>
        </form>
      )}

      {/* Table List */}
      <div>
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-surface-secondary/60 text-xs uppercase text-text-tertiary border-b border-border">
            <tr>
              <th className="px-6 py-3.5 font-semibold">Nombre</th>
              <th className="px-6 py-3.5 font-semibold">Moneda</th>
              <th className="px-6 py-3.5 font-semibold">Visibilidad</th>
              <th className="px-6 py-3.5 font-semibold">Estado</th>
              <th className="px-6 py-3.5 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paymentMethods.map(mp => {
              const branch = branches.find(s => s.id === mp.branch_id);
              return (
                <tr key={mp.id} className="hover:bg-surface-hover/30 transition-colors">
                  <td className="px-6 py-3.5 font-bold text-text-primary flex items-center gap-2">
                    <span>{mp.name}</span>
                    <span className="px-2 py-0.5 rounded-md bg-surface-tertiary text-text-secondary font-mono text-[10px] font-bold border border-border">
                      {mp.currency}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-text-secondary text-xs">{mp.currency}</td>
                  <td className="px-6 py-3.5 text-text-secondary text-xs">{branch ? branch.name : 'Global'}</td>
                  <td className="px-6 py-3.5">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${mp.is_active !== false ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'}`}>
                      {mp.is_active !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 text-right">
                    {canManageConfig && (
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="xs" icon={Pencil} onClick={() => openEdit(mp)} aria-label="Editar" />
                        <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDelete(mp.id)} aria-label="Eliminar" />
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {paymentMethods.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-text-tertiary text-xs">No hay métodos de pago registrados</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Eliminar"
      />
    </div>
  );
}
